import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Help.css';
import {
  X, HelpCircle, Users, CheckSquare, Calendar, Package, ShoppingCart,
  DollarSign, Settings, Database, Key, Building2, Search, Plus, Star,
  Globe, Upload, Download, Tag, Pencil, Archive, RefreshCw, Eye, Monitor,
  Moon, Sun, Filter, Layers, ChevronRight, MapPin, Puzzle, LayoutDashboard,
  FolderKanban, Check, FileText, Map, Shield,
} from 'lucide-react';

// ─── Section definitions ───────────────────────────────────────────────────────

interface FeatureCard {
  name: string;
  location: string;
  looks: string;
  does: string;
}

interface HelpSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  summary: string;
  features: FeatureCard[];
}

const SECTIONS: HelpSection[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: <HelpCircle size={16} />,
    summary: 'ProQure is a facility management platform for retirement and care communities. Use the left sidebar to navigate between modules. Each module is scoped to your facility — your data is private and separate from other facilities.',
    features: [
      {
        name: 'Sidebar Navigation',
        location: 'Left edge of the screen',
        looks: 'Dark vertical bar with icons and labels',
        does: 'Click any item to navigate to that module. The active page is highlighted with an orange accent. The sidebar can be collapsed with the arrow button at the bottom.',
      },
      {
        name: 'Global Search',
        location: 'Top bar, center',
        looks: 'Search bar with magnifying glass icon',
        does: 'Type 2+ characters to search across inventory items, vendors, orders, and tasks simultaneously. Click a result to jump to that module.',
      },
      {
        name: 'Theme Toggle',
        location: 'Top bar, right side (sun/moon icon)',
        looks: 'Sun icon in dark mode, moon icon in light mode',
        does: 'Click to instantly switch between dark and light theme. Hold for system preference option. Preference is saved automatically.',
      },
      {
        name: 'Help Button',
        location: 'Top bar, right side (? icon)',
        looks: 'Circle with question mark',
        does: 'Opens this help overlay. Click the X or press Escape to return to the app.',
      },
      {
        name: 'Facility Badge',
        location: 'Top bar, next to page title',
        looks: 'Small blue pill with 📍 and facility name',
        does: 'Confirms which facility\'s data you are viewing. All data in ProQure is scoped to a single facility per login.',
      },
    ],
  },
  {
    id: 'vendors',
    label: 'Vendors',
    icon: <Users size={16} />,
    path: 'Sidebar → Vendors',
    summary: 'The Vendors module is a directory of all service providers, contractors, and suppliers for your facility. Each vendor card shows contact info, service tags, and status badges at a glance.',
    features: [
      {
        name: 'Vendor Cards',
        location: 'Main grid area',
        looks: 'Cards in a responsive grid, each with name, avatar, service pills, and contact details',
        does: 'Each card summarizes a vendor. It shows the vendor logo (or a building placeholder), 24/7 and Main badges, service category pills, phone/email/website links, and a notes preview. Click any card to open the full detail panel.',
      },
      {
        name: 'Slide-out Detail Panel',
        location: 'Right side of screen, appears on card click',
        looks: 'Full-height slide-in panel from the right',
        does: 'Shows complete vendor information including logo management (upload file or enter URL), all contact details, service tags, account number, hours, and notes. Contains Edit and Delete action buttons.',
      },
      {
        name: 'Vendor Logo',
        location: 'In the Slide-out Detail Panel, top section',
        looks: '96×96 rounded square with Upload File / From URL tabs',
        does: 'Upload any image (PNG, JPG, WebP, SVG — max 5 MB) or paste a public image URL. The logo appears as a 40×40 avatar on the vendor card alongside the vendor name.',
      },
      {
        name: 'Service Category Filters',
        location: 'Above the vendor grid, below the search bar',
        looks: 'Colored pill buttons — "All" plus one pill per category. Collapsed by default.',
        does: 'Click one or more category pills to filter vendors to only those offering those services (additive OR filter). Active pills glow with their category color. Click "All" to clear. Use "Show all ▼" to expand when there are many categories.',
      },
      {
        name: 'Search Bar',
        location: 'Above the filter bar',
        looks: 'Rounded search input, up to 380px wide',
        does: 'Searches vendor name and contact name in real time (server-side). Works together with category filters.',
      },
      {
        name: 'Add Vendor',
        location: 'Top-right of Vendors page',
        looks: 'Blue "Add Vendor" button with + icon',
        does: 'Opens a modal form with all vendor fields: name, service categories, contact person, vendor phone/email, account number, hours, website, and notes. Check "Main Vendor" or "24/7 Service" toggles at the top.',
      },
      {
        name: 'Manage Services',
        location: 'Top-right, next to Add Vendor',
        looks: 'Ghost "Manage Services" button with gear icon',
        does: 'Opens a modal to create, rename, recolor, and delete service categories. Each category gets a custom color that appears on vendor pills throughout the app.',
      },
      {
        name: '24/7 Badge',
        location: 'Top-right corner of each vendor card',
        looks: 'Small green pill reading "24/7"',
        does: 'Indicates the vendor provides 24-hour emergency service. Set via the "24/7 Service" checkbox when adding or editing the vendor.',
      },
      {
        name: 'Main Vendor Badge',
        location: 'Top-right corner of each vendor card (below 24/7)',
        looks: 'Small amber pill reading "Main", and a ⭐ star next to the vendor name',
        does: 'Marks this vendor as the primary/preferred provider. Use it to flag go-to vendors for each service type.',
      },
      {
        name: 'Website Link',
        location: 'Inside vendor card contact details',
        looks: 'Globe icon + clickable domain name in blue',
        does: 'Opens the vendor\'s website in a new tab. Protocols (https://) are normalized automatically so you can enter just the domain.',
      },
      {
        name: '⭐ Preferred Vendors (Pro+)',
        location: 'Vendor card — star icon in the card header',
        looks: 'Gold star icon on the card; preferred vendors sort to the top of the grid',
        does: 'Mark any vendor as preferred using the star icon. Preferred vendors sort to the top of the vendor grid and appear first in the order supplier dropdown, making it easy to reach your go-to providers quickly.',
      },
      {
        name: '📅 Contract Tracking (Pro+)',
        location: 'Vendor detail panel → Contract section',
        looks: 'Date pickers for contract start and expiry; colored expiry badge on the vendor card (green/amber/red)',
        does: 'Set contract start and expiry dates per vendor. The card shows a color-coded badge: green if the contract is current, amber if expiring within 90 days, and red if expired. An alert banner appears at the top of the Vendors page when any contract expires within 30 days.',
      },
      {
        name: '👥 Multiple Contacts (Pro+)',
        location: 'Vendor detail panel → Contacts section',
        looks: 'Tabbed contact form with Primary, Secondary, Emergency, and After-Hours tabs',
        does: 'Add up to four contacts per vendor: primary, secondary, emergency, and after-hours. Each contact has its own name, phone, and email. Standard tier is limited to one contact. Pro and Enterprise tiers have full multi-contact support.',
      },
      {
        name: '✉️ Share via Email (Pro+)',
        location: 'Vendor detail panel → Share button → Email option',
        looks: 'Share button on the vendor card or detail panel; email compose modal',
        does: 'Send a formatted email containing the vendor\'s full contact details to any email address. Useful for sharing vendor information with staff or contractors who do not have ProQure access.',
      },
      {
        name: '🏢 Share to Facility (Pro+)',
        location: 'Vendor detail panel → Share button → Share to Facility option',
        looks: 'Facility picker modal listing other facilities in your organization',
        does: 'Share a vendor record to another facility in your organization. The receiving facility\'s manager receives a notification and must approve or deny the incoming vendor before it appears in their vendor list.',
      },
      {
        name: '📱 Share via SMS (Pro+)',
        location: 'Vendor detail panel → Share button → SMS option',
        looks: 'Phone number input modal with a Send button',
        does: 'Send vendor contact information as an SMS text message to any phone number. Requires Twilio to be configured in the system. Only available on Pro and Enterprise plans. Trial Twilio accounts can only send to verified numbers.',
      },
      {
        name: '🤖 AI Vendor Comparison (Enterprise)',
        location: 'Vendors page → AI Compare button (top toolbar)',
        looks: 'Modal with ranked vendor cards and analysis summary',
        does: 'Claude analyzes all vendors in your directory and ranks them by value, reliability, and coverage gaps. Identifies which service categories have only one vendor (single-source risk) and highlights preferred vendors with the strongest profiles.',
      },
      {
        name: '🚩 Risk Flags (Enterprise)',
        location: 'Vendor cards — badge overlays',
        looks: 'Red or amber flag badges on cards with expired contracts or missing contact info',
        does: 'Automatically displays risk badges on vendor cards when a contract is expired or missing, or when no contact information is on file. Helps identify vendors that need immediate attention before you call on them.',
      },
      {
        name: '📄 Contract Upload (Pro+)',
        location: 'Vendor detail panel → Contract section → Upload button',
        looks: 'File upload button accepting PDF; uploaded file listed with version number and upload date',
        does: 'Upload PDF contracts for a vendor with automatic version history. Each upload is stored in Cloudinary and linked to the vendor. You can view or download any version at any time from the contract section.',
      },
      {
        name: '🔍 AI Contract Analysis (Enterprise)',
        location: 'Vendor detail panel → Contract section → Analyze button',
        looks: 'Analyze button next to an uploaded contract; results appear in a structured card below',
        does: 'Claude reads the uploaded PDF contract and extracts key terms: payment terms, renewal clauses, liability caps, termination conditions, and red flags. Also assigns an overall risk level (Low / Medium / High) to help you understand the contract at a glance without reading the full document.',
      },
      {
        name: '⚖️ Contract Comparison (Enterprise)',
        location: 'Vendor detail panel → Contract section → Compare Versions button (visible with 2+ uploads)',
        looks: 'Side-by-side modal showing two contract versions with diff-style highlighting',
        does: 'Select any two uploaded contract versions to compare them side by side. Highlights price changes, new clauses, and removed clauses. Claude provides a recommendation on whether the newer version is more or less favorable than the previous one.',
      },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: <CheckSquare size={16} />,
    path: 'Sidebar → Tasks',
    summary: 'The Tasks module consolidates all director tasks, scheduled maintenance, facility key tracking, resident room records, and work order management in a single tabbed interface.',
    features: [
      {
        name: 'Reminders Tab',
        location: 'First tab in Tasks',
        looks: 'List of task cards with priority colors and status labels',
        does: 'Shows all active director tasks and reminders. Each task shows title, priority (High/Medium/Low), due date, status, location, and notes. Tasks overdue turn red. Click to expand details.',
      },
      {
        name: 'Archived Tasks Tab',
        location: 'Second tab in Tasks',
        looks: 'Grayed list of completed/archived tasks',
        does: 'Stores tasks that have been archived. Each archived task can be restored to active status. Useful for auditing past work.',
      },
      {
        name: 'Schedule Tab',
        location: 'Third tab in Tasks',
        looks: 'Table with task names on the left and 12 monthly checkboxes on the right',
        does: 'Yearly preventive maintenance schedule. Each row is a maintenance task with its recurrence (monthly, quarterly, semi-annual, annual). Check boxes to mark months complete.',
      },
      {
        name: '📋 Compliance Tracker (All)',
        location: 'Tasks → Schedule tab',
        looks: '12-month grid — task names in rows on the left, columns for each month on the right',
        does: 'Tracks all scheduled maintenance tasks across the full year. Each cell in the grid shows the status for that task in that month, color-coded for instant status review. Essential for passing facility compliance inspections.',
      },
      {
        name: '🔵 Cell States (All)',
        location: 'Tasks → Schedule tab → month cells in the grid',
        looks: 'Colored cells: gray (empty), amber dot (scheduled), green check (completed), red X (missed), blue highlight (due this month)',
        does: 'Each cell in the compliance grid represents one month for one task. Empty (gray) means not yet scheduled. Amber dot means scheduled but not yet done. Green check means completed. Red X means the deadline passed without completion. Blue highlight marks the current month.',
      },
      {
        name: '➕ Add Compliance Task (All)',
        location: 'Tasks → Schedule tab → Add Task button',
        looks: 'Modal with task name, emoji icon picker, frequency selector, vendor dropdown, and notes field',
        does: 'Create a new recurring maintenance task. Set a name, choose an emoji icon, pick a frequency (monthly, quarterly, semi-annual, yearly, or custom interval in months), assign a vendor, and add notes. The task appears as a new row in the compliance grid.',
      },
      {
        name: '↕️ Drag to Reorder (All)',
        location: 'Tasks → Schedule tab → left edge of each task row',
        looks: 'Drag handle icon (six dots) on the left side of every row',
        does: 'Click and drag the handle to reorder tasks in the compliance grid. The new order is saved immediately. Useful for grouping related tasks together or prioritizing high-frequency items at the top.',
      },
      {
        name: '✅ Mark Complete (All)',
        location: 'Tasks → Schedule tab → click a task row to expand it → Mark Complete button',
        looks: 'Expanded row with a green "Mark Complete" button and completion notes field',
        does: 'Expand any task row and click Mark Complete to record that month\'s maintenance as done. The cell turns green with a check mark. Marking a task complete automatically creates a QAPI compliance entry in the Reports module.',
      },
      {
        name: '✏️ Edit Next Due Date (All)',
        location: 'Tasks → Schedule tab → expand a task row → pencil icon next to Next Due date',
        looks: 'Pencil icon next to the Next Due date label in the expanded task row',
        does: 'Click the pencil icon to open a date picker and change when the task is next due. Use this to adjust for holidays, vendor availability, or inspection timing.',
      },
      {
        name: '🗓️ Schedule Button (All)',
        location: 'Tasks → Schedule tab → month cell → Schedule option',
        looks: 'Date picker that appears when you click an empty or scheduled cell',
        does: 'Set the exact date for the next occurrence of a task within a given month. Saves the scheduled date to the cell, changing its state from empty (gray) to scheduled (amber dot).',
      },
      {
        name: '📎 Attach Report (All)',
        location: 'Tasks → Schedule tab → expand a task row → Attach Report button',
        looks: 'File upload button accepting PDF or image; attached file appears as a link in the row',
        does: 'Upload a PDF or image report for a completed maintenance task. The file is stored in Cloudinary and linked to the task completion record. Attached reports appear in Reports → Compliance Reports tab for easy retrieval during inspections.',
      },
      {
        name: '🖨️ PDF Export (All)',
        location: 'Tasks → Schedule tab → Export PDF button (top-right of the tab)',
        looks: 'Export PDF button; generates a printable full-year compliance grid',
        does: 'Exports the entire 12-month compliance grid as a formatted PDF document. Includes all task names, completion status, and cell colors. Print it directly for surveyors and state inspectors who require a paper compliance record.',
      },
      {
        name: '🤖 AI Compliance Summary (Enterprise)',
        location: 'Tasks → Schedule tab → AI Summary button',
        looks: 'AI Summary button; results appear in a card below the grid',
        does: 'Claude analyzes your full compliance grid and generates a plain-English status report for your facility. Identifies overdue tasks, tasks at risk of being missed, patterns of repeated misses, and recommends corrective actions. Useful for preparing for surveys or reporting to leadership.',
      },
      {
        name: 'Facility Keys Tab',
        location: 'Fourth tab in Tasks',
        looks: 'Table of keys with hook numbers, key codes, and type badges',
        does: 'Inventory of all physical keys in the facility. Tracks grand master, new master, sub-master, and standard keys. Each key row shows hook number, key code, and area.',
      },
      {
        name: 'Resident Rooms Tab',
        location: 'Fifth tab in Tasks',
        looks: 'Table of room numbers with resident names and key codes',
        does: 'Room-by-room key assignment tracking. Links each room to a resident name and their assigned key code. Add notes per room.',
      },
      {
        name: 'Task Manager Tab',
        location: 'Sixth tab in Tasks',
        looks: 'Work order table with status and priority color indicators',
        does: 'Live mirror of Reqqer work orders imported into ProQure. Shows req ID, title, location, assignee, status, and priority. Use the Sync button to pull latest data from the Reqqer integration.',
      },
    ],
  },
  {
    id: 'facility-keys',
    label: 'Facility Keys',
    icon: <Key size={16} />,
    path: 'Sidebar → Tasks → Facility Keys tab',
    summary: 'Facility Keys is a sub-module inside the Tasks page. It tracks all physical keys in the building — from grand master keys down to individual room keys.',
    features: [
      {
        name: 'Key Inventory Table',
        location: 'Facility Keys tab inside Tasks',
        looks: 'Table with columns: Hook #, Key Code, Area, Type badges',
        does: 'Complete list of every key in the facility. Type badges (Grand Master, New Master, Sub Master, Standard) color-coded for quick identification.',
      },
      {
        name: 'Add Key',
        location: 'Top of the Facility Keys tab',
        looks: 'Button or inline form',
        does: 'Add a new key record with hook number, key code, area description, and key type classification.',
      },
      {
        name: 'Resident Rooms Link',
        location: 'Fifth tab: Resident Rooms',
        looks: 'Table of rooms with resident names and key assignments',
        does: 'Maps each room number to the resident occupying it and the key code they\'ve been issued. Maintains accountability for physical key distribution.',
      },
      {
        name: '🪝 Hook Board View (Pro+)',
        location: 'Tasks → Facility Keys tab → Board view (default)',
        looks: 'Visual pegboard grid showing each key as a colored tile organized by tier',
        does: 'Displays all facility keys as a visual pegboard, mimicking a physical key board. Keys are grouped and color-coded by tier (Grand Master blue, New Master green, Sub Master purple, Not on Master gray). Click any key tile to see its detail strip and check-out controls. This is the default view for Pro and Enterprise plans.',
      },
      {
        name: '📋 Table View (All)',
        location: 'Tasks → Facility Keys tab → Table view',
        looks: 'Traditional table with columns: Hook #, Key Code, Area, Tier badge, Status',
        does: 'Shows all facility keys in a sortable table format. Displays hook number, key code, area description, tier badge, and current status (available or checked out). Available on all plans and is the only view on Standard tier.',
      },
      {
        name: '🔄 View Toggle (Pro+)',
        location: 'Tasks → Facility Keys tab → top-right toggle buttons',
        looks: 'Two icon buttons: grid (board) and list (table); active view is highlighted',
        does: 'Switch between the visual Hook Board and the traditional Table view. Your preference is saved automatically and restored on next visit. Only available on Pro and Enterprise plans.',
      },
      {
        name: '🏷️ Key Tiers (All)',
        location: 'Facility Keys — tier badges on each key',
        looks: 'Colored tier badges: blue (Grand Master), green (New Master), purple (Sub Master), gray (Not on Master)',
        does: 'Keys are classified into four tiers to reflect their access level. Grand Master keys open everything. New Master keys open most areas. Sub Master keys open a subset of areas. Not on Master keys are standalone or resident keys. Tier is set when adding or editing a key.',
      },
      {
        name: '🔑 Check Out / Return (All)',
        location: 'Facility Keys → click a key tile (board view) or row (table view) → detail strip',
        looks: 'Expanded detail strip below the key with Check Out and Return buttons and a name field',
        does: 'Click a key to expand its detail strip. Enter the person\'s name and click Check Out to record who has the key. Click Return when the key comes back. Each transaction is timestamped. The key tile or row shows the current holder\'s name and checkout duration.',
      },
      {
        name: '⚠️ Overdue Alert (Pro+)',
        location: 'Tasks → Facility Keys tab — amber banner at top of the page',
        looks: 'Amber warning banner listing keys that have been out more than 10 days',
        does: 'An amber alert banner automatically appears at the top of the Facility Keys tab when any key has been checked out for more than 10 days without being returned. Lists the key code, area, and the person who has it. Also surfaces on the Dashboard as an alert.',
      },
      {
        name: '📜 Key History Log (Enterprise)',
        location: 'Tasks → Facility Keys tab → click a key → History tab in detail strip',
        looks: 'Scrollable list of check-out and return events with timestamps and person names',
        does: 'Full audit trail of every check-out and return event for a key. Each entry shows the action (checked out / returned), the person\'s name, and the exact date and time. Useful for security audits and investigating missing keys. Available on Enterprise plans only.',
      },
      {
        name: '📷 QR Codes (Enterprise)',
        location: 'Tasks → Facility Keys tab → key detail strip → QR Code button',
        looks: 'QR Code button; opens a printable QR code image for the key hook',
        does: 'Generate a printable QR code for any key hook. Scan the QR code with any phone to pull up the key\'s record instantly. Print and attach QR codes to your physical key board for fast look-up without typing. Available on Enterprise plans only.',
      },
      {
        name: '🤖 AI Security Audit (Enterprise)',
        location: 'Tasks → Facility Keys tab → AI Audit button (top toolbar)',
        looks: 'AI Audit button; results appear in a modal with flagged items and recommendations',
        does: 'Claude analyzes your full key inventory and flags security concerns: keys currently overdue, keys with no assigned area, multiple keys held by the same person, and gaps in coverage (areas with no keyholder on record). Returns a prioritized list of recommendations. Available on Enterprise plans only.',
      },
    ],
  },
  {
    id: 'catalogue',
    label: 'Catalog',
    icon: <Package size={16} />,
    path: 'Sidebar → Inventory',
    summary: 'The Inventory module has four tabs: Catalog (all items, parts, and consumable supplies), Facility Supplies (tracked assets like beds, wheelchairs, and paint), Pending Imports (Chrome extension review queue), and Facility Map (interactive floor plan editor). All tabs are scoped to your facility.',
    features: [
      {
        name: 'Item Cards Grid',
        location: 'Catalog tab, right panel',
        looks: 'Responsive grid of cards — each showing a category-tinted image area, item name, category, quantity, and price',
        does: 'Hover over any card to reveal a 2×2 action overlay: Edit (blue), View Details (green), Add to Order (amber), Delete (red). Click outside the actions to open the item detail page.',
      },
      {
        name: 'Category Sidebar',
        location: 'Left of the item grid',
        looks: 'Vertical list of icon + category name buttons, collapsible',
        does: 'Click a category to filter items. Click "All Items" to clear. Collapse to icon-only mode with the arrow to give more grid space.',
      },
      {
        name: 'Low Stock Badge',
        location: 'Bottom-left of item card image',
        looks: 'Amber pill reading "⚠ Low Stock"',
        does: 'Appears when the item\'s quantity is at or below its reorder point. Go to item detail to create an order.',
      },
      {
        name: 'Add Item Modal',
        location: 'Opens from "Add Item" button (top-right)',
        looks: 'Two-column modal: left has all input fields, right has an image placeholder',
        does: 'Fill in name (required), category (required), product link, part number, price, supplier, used-for, department, sold-in quantity, unit, storage location, reorder point, barcode, status, description, and notes.',
      },
      {
        name: 'Item Detail Page',
        location: '/inventory/item/:id',
        looks: 'Two-column page: details on left, image and stock summary on right',
        does: 'Full view of all item fields. Contains Edit, Order, and Delete buttons. Breadcrumb navigation back to Inventory.',
      },
      {
        name: 'Add to Order Flow',
        location: 'ShoppingCart hover action on card, or Order button on detail page',
        looks: 'Small modal pre-filled with item name and supplier',
        does: 'Enter quantity and vendor name to instantly create a pending purchase order linked to the item.',
      },
      {
        name: 'USB Barcode Scanner',
        location: 'Barcode field in the Add/Edit Item modal',
        looks: 'Text input labeled "Barcode"',
        does: 'Plug in a USB barcode scanner, click the field, and scan — the barcode auto-populates via keyboard events ending with Enter. No extra setup needed.',
      },
      {
        name: 'Camera Barcode Scanner',
        location: 'Camera icon button next to the Barcode field',
        looks: 'Modal with live camera viewfinder and a blue scanning line animation',
        does: 'Grant camera permission and point at any barcode or QR code. Detection is automatic — the value fills the barcode field instantly.',
      },
      {
        name: 'Facility Supplies Tab',
        location: 'Inventory → Facility Supplies tab',
        looks: 'Six type tabs: Paint, Beds, Wheelchairs, Lifts, HVAC, Other — each with a table',
        does: 'Track non-consumable facility assets. Paint records include color code, finish, and room/area. Equipment records include brand, model, serial number, and last service date. All track quantity, condition, and location.',
      },
      {
        name: 'Category Manager',
        location: 'Inventory → "Categories" button (toolbar, next to Add Item)',
        looks: 'Two-panel modal: category tree on the left, edit form on the right',
        does: 'Create, edit, and delete inventory categories. The left panel shows the full category hierarchy — subcategories are indented under their parent. Hover any category to reveal pencil (edit) and trash (delete) icons. Click a category to load it in the right-hand edit form, where you can change the name, parent category, and icon (choose from 20 preset Lucide icons). At the bottom of the left panel, click "Add New Category" to create a fresh category. If you delete a category that has items assigned to it, ProQure shows a warning and asks you to select a destination category — items are moved there automatically before the deletion completes.',
      },
    ],
  },
  {
    id: 'financials',
    label: 'Financials',
    icon: <DollarSign size={16} />,
    path: 'Sidebar → Financials',
    summary: 'The Financials module provides an overview of facility spending, budget tracking, and cost summaries derived from orders and vendor invoices.',
    features: [
      {
        name: 'Spend Overview',
        location: 'Main content area',
        looks: 'KPI cards and charts showing spend totals',
        does: 'Summarizes total purchase order spend by period, category, and vendor. Helps track against departmental budgets.',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <Settings size={16} />,
    path: 'Sidebar → Settings',
    summary: 'Settings manages your account, users, service categories, facility configuration, and app preferences. Use the left tab list to navigate between setting groups.',
    features: [
      {
        name: 'Profile Tab',
        location: 'Settings → Profile',
        looks: 'Form with first name, last name, email fields and password change section',
        does: 'Update your personal account information and change your login password.',
      },
      {
        name: 'Users Tab',
        location: 'Settings → Users (admin only)',
        looks: 'Table of all users with role badges and status',
        does: 'Admins can view all users, change roles, activate/deactivate accounts, and invite new users to the facility.',
      },
      {
        name: 'Services Tab',
        location: 'Settings → Services',
        looks: 'List of color-coded service categories with add/edit/delete controls',
        does: 'Create and manage the vendor service categories shown throughout the app. Each category gets a custom color. Import/export categories as CSV or JSON.',
      },
      {
        name: 'Appearance Tab',
        location: 'Settings → Appearance',
        looks: 'Three theme cards: Dark, Light, System — click to select',
        does: 'Switch the app color theme. Dark (default) uses the deep navy palette. Light uses a white/slate palette. System follows your OS dark/light preference automatically.',
      },
      {
        name: 'Facility Tab',
        location: 'Settings → Facility',
        looks: 'Facility card with logo, full address/contact form, and a Sister Facilities section below (admin only)',
        does: 'Complete facility profile management. The card shows your facility logo (click to upload a PNG/JPG/WebP/SVG — max 5 MB), facility name, active status badge, and all contact and compliance fields: address, city, state, ZIP, phone, email, timezone, facility type (Assisted Living, Skilled Nursing, etc.), number of beds, state license number, and notes. Admins can click Edit on any card to enter edit mode with a full save/cancel flow. Non-admins see a read-only view with a lock message directing them to their administrator.',
      },
      {
        name: 'Facility Logo Upload',
        location: 'Settings → Facility → logo square (top-left of card, edit mode)',
        looks: '80×80 dashed square with a building icon placeholder; hover shows an upload overlay',
        does: 'Click the logo square while in edit mode to open a file picker. Accepted formats: PNG, JPG, WebP, SVG (max 5 MB). The uploaded logo is stored server-side and appears on the facility card immediately after saving.',
      },
      {
        name: 'Sister Facilities',
        location: 'Settings → Facility → below your own facility card (admin only)',
        looks: 'Section header "Sister Facilities" with an "Add Facility" button, followed by one card per sister facility',
        does: 'Admins can view and edit all other facilities in the organization in the same format as their own facility card. Each sister facility card has the same logo, fields, and edit flow. Use this to manage a multi-site organization from a single admin account.',
      },
      {
        name: 'Add Facility',
        location: 'Settings → Facility → "Add Facility" button (admin only)',
        looks: 'Blue "Add Facility" button with + icon in the Sister Facilities header',
        does: 'Opens a small modal asking for the new facility\'s name. After creating it, the new facility card appears in the Sister Facilities list where you can immediately fill in all details.',
      },
      {
        name: 'Notifications Tab',
        location: 'Settings → Notifications',
        looks: 'Toggle switches for notification types',
        does: 'Configure which events trigger in-app and email notifications for your account.',
      },
    ],
  },
  {
    id: 'import-export',
    label: 'Import & Export',
    icon: <Database size={16} />,
    path: 'Settings → Import / Export',
    summary: 'ProQure supports full data import and export for all major data tables. Use exports for backups or to migrate data. Use imports to bulk-load data from spreadsheets or the Windows ProQure app.',
    features: [
      {
        name: 'Export Data',
        location: 'Settings → Import / Export → Export section',
        looks: 'Buttons per data type: "Export JSON" and "Export CSV"',
        does: 'Downloads a snapshot of the selected table (vendors, catalog, schedule, keys, rooms, or tasks) in your chosen format. JSON includes all fields; CSV is spreadsheet-friendly.',
      },
      {
        name: 'Import Data',
        location: 'Settings → Import / Export → Import section',
        looks: 'File picker buttons for JSON and CSV, plus a mode toggle',
        does: 'Upload a JSON or CSV file to bulk-import records. Use Merge mode to add new records and skip duplicates. Use Replace mode to clear existing data and reload entirely.',
      },
      {
        name: 'Import from SQLite (.db file)',
        location: 'Settings → Import / Export → SQLite import',
        looks: '"Import from .db file" button',
        does: 'Upload a .db file exported from the ProQure Windows desktop app. ProQure auto-discovers the tables and imports vendors, keys, catalog, and other data in a single step.',
      },
      {
        name: 'Service Category Import / Export',
        location: 'Settings → Services → Import / Export section',
        looks: 'Export CSV/JSON buttons and Import CSV/JSON file pickers',
        does: 'Separately import or export the color-coded service categories used in the Vendors module. Supports the same merge/replace modes as the main import tool.',
      },
    ],
  },
  {
    id: 'barcode',
    label: 'Barcode Scanner',
    icon: <Layers size={16} />,
    path: 'Inventory → Add Item modal → Barcode field',
    summary: 'ProQure supports two barcode input methods in the Add/Edit Item modal: USB barcode scanner (plug-and-play, no setup) and camera barcode scanner (uses your device camera with @zxing/browser auto-detection).',
    features: [
      {
        name: 'USB / Bluetooth Scanner',
        location: 'Barcode field in the Add/Edit Item modal',
        looks: 'Standard text input — the scanner acts as a keyboard',
        does: 'Plug in a USB or Bluetooth HID barcode scanner. Click the Barcode field, then scan a product. The scanner types the barcode digits rapidly and sends Enter — the field auto-populates. Compatible with all standard 1D/2D barcode scanners.',
      },
      {
        name: 'Camera Scanner',
        location: 'Camera icon button next to the Barcode field in Add/Edit Item modal',
        looks: 'A modal with a live camera feed and animated blue scan line',
        does: 'Click the camera icon, grant browser camera permission, and hold a barcode in front of the camera. Supports Code 128, Code 39, EAN-13, UPC-A, QR codes, and many more. Detection is fully automatic — no button press needed.',
      },
    ],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: <ShoppingCart size={16} />,
    path: 'Sidebar → Orders',
    summary: 'The Orders module tracks all procurement requests from initial placement through delivery. The Active Orders table shows all open orders in a single, rich spreadsheet-style view. Fully delivered orders are moved to the Completed archive.',
    features: [
      {
        name: 'Active Orders Table',
        location: 'Orders → Active Orders tab',
        looks: 'Wide scrollable table with 14 columns per row',
        does: 'Displays every open order with full detail: product image hover, web link, quantity, placed/ordered/delivered toggle checkboxes, placed date, progress pill, email button, supplier, category, and PO#. Rows can be selected via checkboxes for bulk operations.',
      },
      {
        name: 'Status Columns (Placed / Ordered / Delivered)',
        location: 'Columns 6–8 of the Active Orders table',
        looks: 'Green checkmark (✓) or red X (✗) icon buttons',
        does: 'Each status must be enabled in order. Click Placed to record today as the placed date. Click Ordered only after Placed is checked. Click Delivered only after Ordered is checked. Turning off Placed also clears Ordered and Delivered. All date fields update automatically.',
      },
      {
        name: 'Image Hover',
        location: 'Image (📷) column in the Active Orders table',
        looks: 'Small camera icon; hovering shows a 120×120 product photo popup',
        does: 'Hover over the camera icon to preview the catalog item\'s product image. The photo is pulled from the linked catalog item. If no image exists the icon is dimmed.',
      },
      {
        name: 'Progress Pill',
        location: 'Completed column in the Active Orders table',
        looks: 'Colored percentage badge: gray 0%, yellow 33%, orange 66%, green 100%',
        does: 'Shows at a glance how far along the order is through the three status checkpoints. 100% (all three checked) means the item is ready to be moved to Completed.',
      },
      {
        name: 'PO# Grouping',
        location: 'Active Orders table, rows with matching PO#',
        looks: 'A blue-tinted group header row "PO# [number] — N items — Supplier" above indented item rows',
        does: 'When multiple items share the same PO number they are visually grouped under a collapsible header. Click the chevron on the header row to collapse or expand the group. Items without a PO# appear below all groups as ungrouped rows.',
      },
      {
        name: 'Add PO#',
        location: 'Orders toolbar → "Add PO#" button',
        looks: 'Modal with a PO# text input and a checklist of items currently without a PO#',
        does: 'Enter a PO number and select one or more items from the list, then click Assign. The PO# is applied to all selected items and they immediately appear in a PO group in the table.',
      },
      {
        name: 'Move Completed',
        location: 'Orders toolbar → "Move Completed (N)" button',
        looks: 'Button showing the count of fully delivered orders',
        does: 'Moves all orders where Delivered is checked from the Active tab to the Completed archive. Sets the completed date to today. The button is disabled when no orders are fully delivered.',
      },
      {
        name: 'Completed Tab',
        location: 'Orders → Completed tab',
        looks: 'Month and year dropdowns above a read-only table',
        does: 'Browse all archived orders filtered by month and year. Shows product name, qty, supplier, category, completed date, and PO#. Select rows and click Delete to permanently remove records. Export button downloads the filtered view as CSV.',
      },
      {
        name: 'Approval Email',
        location: 'Email (✉) column in the Active Orders table',
        looks: 'Envelope icon button; green when an email has been sent for that item',
        does: 'Click the envelope to open an email composition modal pre-filled with the order details and the template configured in Settings → Orders. Edit the To, Subject, and Body fields, then click Send Email — this opens your email client and marks the order as emailed. Click "Save as Template" to persist the current body as the new default.',
      },
      {
        name: 'Add from Catalog',
        location: 'Orders → Add Item modal → catalog search bar at top',
        looks: 'Search input with a results dropdown showing matching catalog items',
        does: 'Type 2+ characters to search the catalog. Selecting an item auto-fills the product name, part#, image URL, product link, category, unit price, and unit. The supplier defaults to DSSI if that vendor exists in the system, otherwise the first vendor alphabetically.',
      },
    ],
  },
  {
    id: 'schedule',
    label: 'Calendar',
    icon: <Calendar size={16} />,
    path: 'Sidebar → Calendar',
    summary: 'The Schedule page is a facility-wide calendar that aggregates calendar events, director tasks with due dates, and maintenance tasks. Toggle between Month and Week views using the buttons in the top-right of the page.',
    features: [
      {
        name: 'Month View',
        location: 'Schedule → Month button (top-right)',
        looks: 'Standard 7-column calendar grid. Events appear as colored pills inside each day cell.',
        does: 'Shows all events for the visible month. Each day cell is clickable — clicking an empty day opens the Add Event modal pre-filled with that date. Up to 3 event pills show per cell; "+N more" appears if there are additional events. Navigate months with the ← → arrows.',
      },
      {
        name: 'Week View',
        location: 'Schedule → Week button (top-right)',
        looks: '7-column grid with hourly rows from 6 AM to 9 PM, plus an all-day row at the top',
        does: 'Shows the selected week with events positioned in their hour slot. All-day events appear in the top row. Click any time slot to add an event at that date and time. Navigate weeks with the ← → arrows.',
      },
      {
        name: 'Event Types & Colors',
        location: 'Visible via the color legend below the navigation bar',
        looks: 'Color-coded event pills — blue, orange, purple, red',
        does: 'Maintenance events are blue, Task events are orange, Meetings are purple. Events whose date has passed and are of type Task or Maintenance turn red (overdue). Completed tasks from the Tasks module appear green.',
      },
      {
        name: 'Add Event',
        location: 'Schedule → "Add Event" button, or click any day/time slot',
        looks: 'Modal with title, date, time, all-day toggle, event type, repeat, and notes fields',
        does: 'Fill in the event details. For non-all-day events, enter a start time and optional end time. Repeat options: None, Daily, Weekly, Monthly, or Annually. Click "Add Event" to save.',
      },
      {
        name: 'Linked Tasks',
        location: 'Calendar — displayed automatically from the Tasks module',
        looks: 'Orange event pills on the days matching task due dates',
        does: 'Director tasks that have a due date are automatically shown on the calendar as orange "Task" events. These cannot be deleted from the calendar — manage them in the Tasks module instead.',
      },
      {
        name: 'Event Detail',
        location: 'Click any event pill (in month or week view)',
        looks: 'Small popup showing date, time, type, repeat info, and notes',
        does: 'View full details for any event. Calendar events can be deleted from this popup. Task-linked events are read-only here — go to Tasks to modify them.',
      },
    ],
  },
  {
    id: 'chrome-extension',
    label: 'Chrome Extension',
    icon: <Puzzle size={16} />,
    path: 'Settings → Import / Export → Chrome Extension',
    summary: 'The ProQure Web Catalog Helper Chrome extension (v2.0) connects directly to the ProQure.Web server on port 3001. Capture product details from supplier websites with one click and they appear in ProQure as Pending Imports — complete with a badge count and toast notification. A manager or admin can then review, edit, and approve them into the main inventory catalog.',
    features: [
      {
        name: 'Installation',
        location: 'Settings → Import / Export → Chrome Extension section',
        looks: '"Download Extension (v2.0)" button followed by step-by-step instructions',
        does: 'Click "Download Extension (v2.0)" to download a zip of the extension files from the ProQure server. Unzip it, then open chrome://extensions in Chrome, enable Developer mode, and click "Load unpacked" to select the unzipped folder. The ProQure Web Catalog Helper icon will appear in your Chrome toolbar.',
      },
      {
        name: 'Importing a Product',
        location: 'Chrome toolbar — ProQure icon, while viewing a product page',
        looks: 'A small popup showing extracted product name, part#, price, supplier, and an image preview',
        does: 'Navigate to a supported product page (Grainger, HD Supply, Home Depot, McMaster-Carr, or Amazon), then click the ProQure icon. The extension automatically extracts the product data from the page and shows a preview. Click "Import to ProQure Catalog" to send it to Pending Imports.',
      },
      {
        name: 'Supported Sites',
        location: 'Any product detail page on the supported sites',
        looks: 'The extension popup opens with a product preview when on a supported site',
        does: 'Supported sites: Grainger, HD Supply, Home Depot, McMaster-Carr, Amazon (.com, .ca, .co.uk). On unsupported sites, the extension shows "This site is not supported."',
      },
      {
        name: 'Server Connection Status',
        location: 'Inside the extension popup',
        looks: 'Green "✓ Connected to ProQure" or orange "⚠ ProQure server not reachable" banner',
        does: 'The extension pings the ProQure.Web server (port 3001) at startup. If the server is not running or unreachable, the Import button is disabled. Ensure the ProQure server is running before using the extension.',
      },
      {
        name: 'Toast Notification',
        location: 'Bottom-right corner of the Inventory page',
        looks: 'Blue notification banner: "📦 New item from Chrome extension — Review pending imports" with a View button',
        does: 'When the extension sends a new item, ProQure detects it within 10 seconds and shows a toast at the bottom-right of the screen. Click "View" to open the Pending Imports panel immediately, or wait for the toast to auto-dismiss.',
      },
      {
        name: 'Pending Imports Badge',
        location: 'Inventory → Catalog toolbar → "Pending Imports" button',
        looks: 'Button with a red badge showing the count of unreviewed items; polled every 10 seconds',
        does: 'The badge count updates automatically every 10 seconds. Click the button to open the Pending Imports panel where all extension-imported items await review.',
      },
      {
        name: 'Pending Imports Panel',
        location: 'Inventory → Catalog → "Pending Imports" button (top-right)',
        looks: 'Slide-in panel with item cards showing image, name, part#, price, supplier, description preview, and source link',
        does: 'All items from the extension arrive here with status "pending". Every card has two buttons: "Edit & Review" (opens the full edit modal) and "Reject". Clicking anywhere on the card also opens the edit modal. There is no direct Approve button — you must go through Edit & Review to select a category first.',
      },
      {
        name: 'Edit & Review Modal',
        location: 'Inventory → Catalog → Pending Imports panel → "Edit & Review" button',
        looks: 'Full two-column modal identical to the Add Item form, pre-filled with all extension data',
        does: 'Opens the complete edit form pre-filled with name, part#, price, supplier, description (with bullet-point formatting preserved), and image preview. You must select a Category before approving — the field is highlighted in red if you try to approve without one. Three buttons at the bottom: Cancel, Save as Draft, and Save & Approve.',
      },
      {
        name: 'Category Selection (Required)',
        location: 'Edit & Review modal → Category field',
        looks: 'Dropdown with all inventory categories; label turns red if you try to approve without selecting',
        does: 'You must pick a category before clicking "Save & Approve". The category determines where the item appears in the Catalog. If you close without approving, the category selection is not saved — it only applies when you approve.',
      },
      {
        name: 'Description Formatting',
        location: 'Edit & Review modal → Description textarea',
        looks: 'Multi-line textarea showing bullet points on separate lines exactly as received from the supplier website',
        does: 'The description is preserved with all line breaks intact throughout the pipeline — from the extension through the API, database, and edit modal. The textarea uses pre-wrap rendering so each bullet point appears on its own line. The same formatting is preserved in the approved item\'s detail page.',
      },
      {
        name: 'Image Handling',
        location: 'Edit & Review modal → right-hand image panel',
        looks: 'Image preview labeled "Current image (from web)", with "Replace with upload" button and URL input below',
        does: 'The original image URL from the supplier site is shown immediately. You can keep it, replace it with a file upload, or paste a new URL. When you approve, the server attempts to download and cache the image locally. If download fails, the original remote URL is kept as fallback.',
      },
      {
        name: 'Save as Draft',
        location: 'Edit & Review modal → "Save as Draft" button',
        looks: 'Secondary button in the modal footer',
        does: 'Saves all edits (name, price, description, supplier, image URL) back to the pending item without approving. The item stays in Pending Imports for later review. Useful when you want to clean up the data now but approve later.',
      },
      {
        name: 'Save & Approve',
        location: 'Edit & Review modal → "Save & Approve" button',
        looks: 'Primary blue button with a checkmark icon',
        does: 'Saves all edits and immediately moves the item into the main Catalog under the selected category. The pending item disappears from the panel and appears as an Active inventory item. Requires a category to be selected.',
      },
      {
        name: 'Rejecting an Import',
        location: 'Inventory → Catalog → Pending Imports panel → "Reject" button on card',
        looks: 'Red "Reject" button on each item card',
        does: 'Permanently deletes the pending item and its downloaded image from the server. Use this for duplicates, wrong products, or items that do not belong in inventory.',
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard size={16} />,
    path: 'Sidebar → Dashboard',
    summary: 'The Dashboard is your facility command center, providing at-a-glance visibility into operations, upcoming maintenance, weather, and recent activity.',
    features: [
      {
        name: 'Snow Alert Banner',
        location: 'Dashboard top, full-width amber banner',
        looks: 'Amber/yellow warning bar with snowflake emoji',
        does: 'Automatically appears when snow is forecasted in the next 3 days based on your weather location setting. Shows the day and condition. Click X to dismiss for the session.',
      },
      {
        name: 'Upcoming Reminders',
        location: 'Dashboard → top-left panel',
        looks: 'Card with task rows and colored due-date badges',
        does: 'Shows tasks due within the next 7 days. Badges are red for overdue, orange for today, yellow for tomorrow, and blue for future dates. Click "View all" to go to the Tasks page.',
      },
      {
        name: "This Month's Scheduled Maintenance",
        location: 'Dashboard → top-right panel',
        looks: 'Card listing maintenance tasks and vendor names',
        does: 'Shows scheduled maintenance tasks for the current month from the Schedule module.',
      },
      {
        name: 'Metric Cards',
        location: 'Dashboard → horizontal scrollable row',
        looks: 'Row of cards with icons, large numbers, and labels',
        does: 'Shows Director Tasks, Catalog Items, Total Vendors, Active Tickets, Active Orders, and Monthly Spend. The Monthly Spend card shows spend vs. total budget from the Financials settings.',
      },
      {
        name: 'Workboard Tabs',
        location: 'Dashboard → left 60% of lower section',
        looks: 'Tabbed panel with "Open Tickets", "My Approvals", and "Inventory List"',
        does: 'Open Tickets shows task manager items with links, due dates, and assigned users. My Approvals shows orders pending placement. Inventory List shows items below their reorder point.',
      },
      {
        name: 'Activity Feed',
        location: 'Dashboard → right side panel',
        looks: 'Scrollable list of recent system events with badges and timestamps',
        does: 'Shows the last 10 activity log entries. Events include order creation, item additions, vendor additions, and deletions. Timestamps use relative format (e.g. "2 hours ago").',
      },
      {
        name: 'Quick Actions',
        location: 'Dashboard → right side, above Activity Feed',
        looks: 'Row of small buttons: New Item, New Order, New Vendor, New Ticket',
        does: 'One-click navigation shortcuts to create new records in their respective modules.',
      },
      {
        name: '7-Day Weather Forecast',
        location: 'Dashboard bottom-left panel',
        looks: 'Table with emoji, day name, condition, and high/low temperatures',
        does: 'Shows a 7-day forecast using the Open-Meteo API. Requires a weather location to be set in Settings → Appearance → Weather Location. Today\'s row has a blue background. Toggle between °F and °C.',
      },
      {
        name: 'Current Weather Conditions',
        location: 'Dashboard bottom-right panel',
        looks: 'Large temperature display with condition, precipitation, wind, and location',
        does: 'Shows real-time weather data for your configured location. Data is from the Open-Meteo free API and updates when the page loads.',
      },
      {
        name: '🔑 Keys Out KPI (All)',
        location: 'Dashboard → metric cards row',
        looks: 'KPI card showing total keys in the system and how many are currently checked out',
        does: 'Displays the total number of facility keys on the system and how many are currently checked out. Lets you see at a glance whether any keys are unaccounted for without navigating to the Facility Keys tab.',
      },
      {
        name: '🔧 Scheduled Maintenance Card (All)',
        location: 'Dashboard → top panel',
        looks: 'Card listing maintenance tasks due within 30 days, color-coded by urgency',
        does: 'Shows all compliance tracker tasks with a next due date within the next 30 days. Tasks are color-coded: red for overdue, amber for due within 7 days, and green for due later in the month. Click any task to jump to the compliance tracker.',
      },
      {
        name: '🚨 Alerts — Overdue Keys & Expiring Contracts (All / Pro+)',
        location: 'Dashboard → alert banners near the top of the page',
        looks: 'Amber alert banners listing overdue keys or expiring vendor contracts',
        does: 'ProQure automatically surfaces two types of alerts on the Dashboard: overdue facility keys (keys checked out more than 10 days — Pro+) and vendor contracts expiring within 30 days (Pro+). Alerts appear as dismissible amber banners so critical issues are visible the moment you open the app.',
      },
    ],
  },
  {
    id: 'settings-financials',
    label: 'Settings: Financials',
    icon: <DollarSign size={16} />,
    path: 'Sidebar → Settings → Financials tab',
    summary: 'The Financials settings tab lets you define budget categories with monthly spending targets and alert thresholds.',
    features: [
      {
        name: 'Budget Categories',
        location: 'Settings → Financials',
        looks: 'List of cards with category name, monthly amount, and alert percentage',
        does: 'Each budget category tracks a spending area (e.g. Maintenance Supplies, Utilities). The monthly_amount is used to calculate the total facility budget shown on the Dashboard Monthly Spend card.',
      },
      {
        name: 'Alert Threshold',
        location: 'Budget category card or edit form',
        looks: 'Percentage field (default 80)',
        does: 'Sets the percentage of the monthly budget at which an alert should be triggered. For example, 80% means an alert fires when spend reaches 80% of the monthly amount.',
      },
      {
        name: 'Enable/Disable Categories',
        location: 'Budget category card → "Disable"/"Enable" button',
        looks: 'Small gray button next to category name',
        does: 'Inactive categories are grayed out and excluded from budget calculations. Use this to temporarily pause tracking without deleting the category.',
      },
      {
        name: 'Pre-seeded Categories',
        location: 'Auto-loaded on first visit to Financials tab',
        looks: 'Seven default categories appear automatically',
        does: 'If no budgets exist, the system seeds seven default categories: Maintenance Supplies, Maintenance Equipment, Utilities, Administrative, Nursing Supplies, Kitchen/Food, and Cleaning Supplies.',
      },
    ],
  },
  {
    id: 'global-search',
    label: 'Global Search',
    icon: <Search size={16} />,
    path: 'Top bar → Search field (center)',
    summary: 'The Global Search bar lets you search across all modules simultaneously from anywhere in the app.',
    features: [
      {
        name: 'How to Search',
        location: 'Top bar, center',
        looks: 'Search input with magnifying glass icon',
        does: 'Type 2 or more characters to trigger a search. Results appear in a grouped dropdown below the search bar after a 300ms debounce. Press Escape or click outside to close the dropdown.',
      },
      {
        name: 'Search Scope',
        location: 'Global — all modules',
        looks: 'Grouped results with colored type badges',
        does: 'Searches across: Catalog Items (name, part number, description, barcode), Vendors (company name, contact, phone, email), Orders (product name, part number, PO#, supplier), Tasks (title, location, notes), Facility Keys (key code, area), and Schedule Items (task name, vendor name). Returns up to 5 results per type, 30 total.',
      },
      {
        name: 'Navigating Results',
        location: 'Search dropdown',
        looks: 'Grouped rows with badge, title, and gray subtitle',
        does: 'Click any result to navigate to its module. The dropdown closes automatically. Each result shows a colored type badge (e.g. "Catalog", "Vendors"), the item title, and a subtitle with key details.',
      },
    ],
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: <FolderKanban size={16} />,
    path: 'Sidebar → Projects',
    summary: 'The Projects module tracks capital improvement and facility projects from planning through completion. Each project has a status, optional vendor, estimated vs. actual cost, line items for detailed budgeting, and up to three vendor quotes for side-by-side comparison.',
    features: [
      {
        name: 'Project List',
        location: 'Sidebar → Projects',
        looks: 'Table with columns for name, status badge, vendor, estimated cost, actual cost, and dates',
        does: 'Shows all projects for your facility. Use the status filter dropdown to narrow by planning, active, on hold, completed, or cancelled. Click any row to open the detail panel.',
      },
      {
        name: 'Create / Edit Project',
        location: 'Projects → "New Project" button or click a row',
        looks: 'Slide-in panel from the right with form fields',
        does: 'Fill in the project name (required), description, status, assigned vendor, estimated and actual costs, start/end dates, and notes. Managers, admins, and org admins can create and edit projects.',
      },
      {
        name: 'Line Items',
        location: 'Project detail panel → Line Items section',
        looks: 'Rows with description, quantity, unit cost, and auto-calculated total',
        does: 'Break down a project into individual cost line items. Each row auto-calculates its total (quantity × unit cost). Add rows with the "+ Add Line Item" button. The panel shows the grand total across all line items.',
      },
      {
        name: 'Project Quotes',
        location: 'Projects table → click the Quote (📄) cell on any row to expand',
        looks: 'Expandable row showing quote cards and an "Add Quote" form',
        does: 'Attach up to 3 vendor quotes per project. Each quote can include a vendor name, dollar amount, an optional uploaded file (PDF, image, or Word doc), and notes. Quotes are added inline from the expanded row.',
      },
      {
        name: 'Compare Quotes',
        location: 'Expanded quotes row → "Compare" button (visible when 2+ quotes exist)',
        looks: 'Modal with quote cards side by side; lowest quote highlighted in green',
        does: 'Opens a comparison modal showing all quotes for the project side-by-side. The lowest quote is highlighted with a green badge. A savings callout shows the difference between the lowest and next-lowest quote.',
      },
      {
        name: 'Status Badges',
        location: 'Projects table → Status column',
        looks: 'Colored pill badges: blue (planning), green (active), yellow (on hold), gray (completed), red (cancelled)',
        does: 'Visual indicator of the project lifecycle stage. Status is set when creating or editing a project.',
      },
      {
        name: 'Delete Project',
        location: 'Project detail panel → "Delete" button',
        looks: 'Red delete button at the bottom of the panel; requires confirmation',
        does: 'Permanently deletes the project along with all its line items and quotes. A confirmation dialog prevents accidental deletion.',
      },
      {
        name: '🔄 Project Workflow (All)',
        location: 'Projects table → Status column and workflow header',
        looks: '6-stage pipeline: Obtaining Bids → Approval Sent → Approved → Scheduling → Completed → Paid',
        does: 'Every project moves through six defined stages from initial bidding through final payment. Each stage is shown as a status badge. Advancing a project to the next stage updates the badge and timestamps the transition. The workflow ensures nothing falls through the cracks between vendor selection and project close-out.',
      },
      {
        name: '📤 Upload Bid / Proposal (All)',
        location: 'Projects → expanded quote row → Upload button',
        looks: 'File upload button accepting PDF; uploaded file listed with vendor name and amount',
        does: 'Upload a PDF bid or proposal document from a vendor. The file is stored in Cloudinary and linked to the quote record. You can view or download the original document at any time from the quote card.',
      },
      {
        name: '🤖 AI Proposal Extraction (Pro+)',
        location: 'Projects → quote card → Extract with AI button',
        looks: 'Extract button on an uploaded PDF quote; results populate the line items automatically',
        does: 'Claude reads the uploaded PDF bid and automatically extracts line items, quantities, unit costs, totals, and vendor information. Extracted data fills in the quote fields without manual entry. Pro plans get 9 AI extractions per month per facility; Enterprise plans have unlimited extractions.',
      },
      {
        name: '🏗️ Subcontracts (All)',
        location: 'Projects → quote card → Add Subcontract button',
        looks: 'Subcontract cards nested under the main bid card, each with vendor name, amount, and line items',
        does: 'Add subcontractor bids under each main bid card to track the full cost breakdown for a project. Subcontract line items can be extracted with AI (Pro+) or entered manually. The main bid card shows a roll-up total including all subcontracts.',
      },
      {
        name: '📨 Send for Approval (All)',
        location: 'Projects → project detail panel → Send for Approval button',
        looks: 'Send for Approval button; opens an email preview with the selected bid and vendor comparison',
        does: 'Send the project along with the selected bid to your org admin for approval. An email is generated with the project name, selected vendor, bid amount, and a comparison table of all submitted bids. The project status advances to Approval Sent automatically.',
      },
      {
        name: '🤖 AI Bid Analysis (Enterprise)',
        location: 'Projects → expanded quote row → AI Analyze Bids button (visible with 2+ quotes)',
        looks: 'AI Analyze button; results appear as a ranked recommendation card below the quotes',
        does: 'Claude compares all submitted bids for a project and recommends the best value option. Analysis considers total price, line item breakdown, completeness of scope, and any notes. Returns a ranked recommendation with reasoning. Available on Enterprise plans only.',
      },
      {
        name: '✅ Project Completion (All)',
        location: 'Projects → project detail panel → advance status to Completed',
        looks: 'Status dropdown or button to advance to Completed stage',
        does: 'When a project is advanced to Completed status, ProQure automatically creates a QAPI compliance entry in the Reports module. This ensures all completed capital projects are documented in the facility\'s quality assurance record without any extra steps.',
      },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    icon: <Users size={16} />,
    path: 'Top bar → Users icon (person icon between Chat and Help)',
    summary: 'The Users panel shows all users in your organization and lets admins manage accounts, roles, passwords, and email addresses. Access it from the person icon in the top-right toolbar.',
    features: [
      {
        name: 'Users List',
        location: 'Top bar → Users icon',
        looks: 'Full-screen page with a table of users showing name, email, role badge, facility, status, and last login',
        does: 'Admins and org admins see all users across the organization. Managers and viewers see only users at their own facility. Inactive accounts are shown with a dimmed style.',
      },
      {
        name: 'User Roles',
        location: 'Role badge column in the Users table',
        looks: 'Colored pill badges: purple (org_admin), blue (admin), green (manager), gray (viewer)',
        does: 'org_admin has full access across all facilities in the organization. admin has full access within one facility. manager can create and edit most records but cannot manage users. viewer has read-only access.',
      },
      {
        name: 'Change Password',
        location: 'Users table row → "Change Password" action',
        looks: 'Action available to admins; opens a small form',
        does: 'Admins can directly set a new password for any user. The new password must be at least 8 characters. org_admins use a grant-approval flow via Settings for extra security.',
      },
      {
        name: 'Change Email',
        location: 'Users table row → "Change Email" action',
        looks: 'Inline form in the user row',
        does: 'Admins can update a user\'s email address. The system checks for duplicates and rejects the change if the email is already in use.',
      },
      {
        name: 'Deactivate / Reactivate',
        location: 'Users table row → "Deactivate" or "Reactivate" button',
        looks: 'Small gray or green button in the actions column',
        does: 'Deactivated users cannot log in. Their records and history are preserved. Admins can reactivate accounts at any time. You cannot deactivate your own account.',
      },
    ],
  },
  {
    id: 'bom',
    label: '🔩 Bill of Materials',
    icon: <Layers size={16} />,
    path: 'Sidebar → Inventory → select item → Parts / BOM button',
    summary: '🔩 The Bill of Materials (BOM) feature lets you define the component structure of any inventory item. Build a full parts hierarchy — each item can have multiple child parts, and each child can have its own sub-parts, up to 5 levels deep. Use it to track which parts make up a device, piece of equipment, or assembly.',
    features: [
      {
        name: '🌲 Parts Tree (Left Panel)',
        location: 'Left 260px panel of the BOM split-panel view',
        looks: 'Dark sidebar with expandable tree nodes — each node shows a colored dot, item name, and unit price',
        does: 'Shows the full parts hierarchy for the root item. Click ▸ to expand any node and see its sub-parts. Click a node to load its full details in the right panel. The "Add Part" button at the bottom opens the part search modal.',
      },
      {
        name: '📋 Part Details & Pricing (Right Panel)',
        location: 'Right panel of the BOM split-panel view',
        looks: 'Supplier badge, large item name, two metric cards (Unit Price and Total), info fields, and an order section',
        does: 'Shows full details for the selected part: supplier, sub-part count, notes. Two metric cards display the unit price and the total cost (unit price × BOM quantity). The order section lets you choose a custom quantity and shows a live order total before placing the order.',
      },
      {
        name: '➕ Add Part Modal',
        location: 'Bottom of the Parts Tree panel → "Add Part" button',
        looks: 'Blue outlined button that opens a search modal with an inventory search input and results list',
        does: 'Search any inventory item by name or part number. Select the item, set the required quantity, and click "Link Part" to attach it as a child of the selected node (or the root item if none is selected). The tree refreshes automatically.',
      },
      {
        name: '🛒 Order from BOM',
        location: 'Right panel → Order section',
        looks: 'Quantity input, live "Order total: $X.XX" display, and a blue "Add to Order" button',
        does: 'Select a part in the tree, adjust the order quantity (defaults to the BOM quantity for that part), and click "Add to Order". The item is added to your Orders module immediately with the part details and pricing pre-filled.',
      },
      {
        name: '📷 Screenshot',
        location: 'BOM split-panel view',
        looks: '[Image placeholder: Parts tree on the left with colored dots and expand/collapse arrows; detail panel on the right showing supplier badge, metric cards, and order controls]',
        does: 'The split-panel layout gives you a full hierarchical view on the left and actionable detail on the right — without leaving the page.',
      },
    ],
  },
  {
    id: 'facility-map',
    label: 'Facility Map',
    icon: <Map size={16} />,
    path: 'Sidebar → Inventory → Facility Map tab',
    summary: 'The Facility Map is an interactive canvas floor plan editor. Draw and label every room in your building, attach equipment and fire extinguisher records to individual rooms, and overlay a trace image (scanned floor plan or blueprint) to use as a drawing guide.',
    features: [
      {
        name: 'Floor Selector',
        location: 'Facility Map toolbar — top-left dropdown',
        looks: 'Dropdown listing all floors; "Add Floor" option at the bottom',
        does: 'Switch between floors. Each floor has its own canvas with independent room layouts. Add new floors as needed for multi-story buildings.',
      },
      {
        name: 'Drawing Rooms',
        location: 'Facility Map canvas — Polygon tool (default)',
        looks: 'Click-by-click polygon drawing with a dashed preview line connecting back to the start point',
        does: 'Click on the canvas to place vertices and draw the room outline. Click the first vertex again (or press Enter) to close the shape and name the room. Rooms snap to a configurable grid for clean alignment.',
      },
      {
        name: 'Room Labels',
        location: 'Center of each drawn room on the canvas',
        looks: 'Room name text centered inside the room polygon',
        does: 'Room names are displayed centered in the polygon. Click a room to select it and open the Room Detail panel on the right.',
      },
      {
        name: 'Room Detail Panel',
        location: 'Right side — opens when a room is clicked',
        looks: 'Slide-in panel with three tabs: Overview, Equipment, Extinguishers',
        does: 'Shows full room information and lets you manage per-room records. Use the three tabs to switch between room details, equipment inventory, and fire extinguisher records for that specific room.',
      },
      {
        name: 'Overview Tab',
        location: 'Room Detail Panel → Overview tab',
        looks: 'Form with room name, type, notes, and size fields',
        does: 'Edit the room name, type, square footage, and notes. Changes are saved immediately.',
      },
      {
        name: 'Equipment Tab',
        location: 'Room Detail Panel → Equipment tab',
        looks: 'List of equipment items with name, model, serial number, and last service date',
        does: 'Track all equipment installed in the room: HVAC units, nurse call systems, electrical panels, etc. Add, edit, and delete equipment records. Each record includes name, model, serial number, and last service date.',
      },
      {
        name: 'Extinguishers Tab',
        location: 'Room Detail Panel → Extinguishers tab',
        looks: 'List of fire extinguisher records with type, last inspection date, and next due date',
        does: 'Track fire extinguishers located in the room. Each record includes extinguisher type, last inspection date, and next inspection due date. Overdue inspections are highlighted in red.',
      },
      {
        name: 'Multi-Select',
        location: 'Facility Map canvas — hold Ctrl and click rooms',
        looks: 'Selected rooms highlighted with a blue outline; selection count shown in toolbar',
        does: 'Hold Ctrl and click multiple rooms to select them simultaneously. Selected rooms can be moved together or deleted in bulk. Click on empty canvas to deselect all.',
      },
      {
        name: 'Pan and Zoom',
        location: 'Facility Map canvas',
        looks: 'Ctrl+scroll to zoom; middle mouse button drag or Space+drag to pan',
        does: 'Navigate large floor plans by scrolling with Ctrl held to zoom in and out, and panning by holding the middle mouse button or holding Space while dragging. The current zoom level is shown in the toolbar.',
      },
      {
        name: 'Trace Image Overlay',
        location: 'Facility Map toolbar → Trace button',
        looks: 'Semi-transparent image overlaid on the canvas; controls panel for opacity, scale, and X/Y offset',
        does: 'Upload a scanned floor plan, blueprint, or any reference image to use as a drawing guide. Adjust the overlay opacity (10%–100%), scale percentage, and X/Y offset to align it precisely with your canvas. The trace image is saved per-floor and persists across sessions.',
      },
      {
        name: 'Keyboard Shortcuts',
        location: 'Press Shift+? anywhere on the Facility Map canvas',
        looks: 'Modal listing all available keyboard shortcuts',
        does: 'Opens a keyboard shortcuts reference modal. Key shortcuts include: Delete/Backspace to delete selected rooms, Escape to cancel drawing or deselect, Ctrl+A to select all rooms, and Ctrl+Z to undo the last action.',
      },
      {
        name: 'Cut / Subtract Tool',
        location: 'Facility Map toolbar → Cut tool (scissors icon)',
        looks: 'Scissors icon in the drawing toolbar; active cut shape shown with red dashed outline',
        does: 'Draw a cut shape over an existing room to subtract that area from the room polygon. Useful for creating L-shaped or irregularly shaped rooms that cannot be drawn as a simple convex polygon.',
      },
      {
        name: '1:1 Scale and Real-World Dimensions',
        location: 'Facility Map toolbar → Scale input',
        looks: 'Scale input field showing current pixels-per-unit; live dimension labels on rooms',
        does: 'Set a real-world scale (e.g. 1 pixel = 1 foot) so the canvas represents actual room dimensions. Once set, each room displays its calculated width and height in feet. Real-time X/Y coordinates in the toolbar show the cursor position in world units.',
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <FileText size={16} />,
    path: 'Sidebar → Reports',
    summary: 'The Reports module is the central archive for facility compliance documentation. It automatically captures compliance entries whenever maintenance tasks are marked complete or capital projects are closed out, creating a permanent audit trail for state inspectors and surveyors.',
    features: [
      {
        name: 'QAPI Compliance Reports Tab',
        location: 'Reports → QAPI tab (default)',
        looks: 'Table of compliance entries with task name, completion date, status, and attached files',
        does: 'Lists all compliance entries for the facility. Each entry links to the maintenance task or project that generated it. Click any row to expand and see completion notes and attached report files.',
      },
      {
        name: 'Automatic Entry Creation',
        location: 'Created automatically — no manual action needed',
        looks: 'New rows appear in the Reports table after task completions or project closings',
        does: 'A QAPI compliance entry is created automatically when: (1) a compliance task is marked complete in Tasks → Schedule tab, or (2) a project is advanced to Completed status in the Projects module. Both events log the date, responsible party, and any attached documents.',
      },
      {
        name: 'Attached Report Files',
        location: 'Reports table → expand any entry row',
        looks: 'PDF or image file links inside the expanded entry row',
        does: 'Any files attached during a task completion are visible here. Click a file link to view or download the document. These are the same files uploaded via Tasks → Schedule tab → Attach Report.',
      },
      {
        name: 'Filter by Date Range',
        location: 'Reports toolbar — From and To date pickers',
        looks: 'Two date inputs above the reports table',
        does: 'Filter the compliance report list to a specific date range. Useful when preparing documentation for an inspection period or regulatory filing.',
      },
      {
        name: 'Export to PDF',
        location: 'Reports toolbar → Export PDF button',
        looks: 'Export PDF button; generates a formatted compliance report document',
        does: 'Exports the current filtered view of compliance entries as a formatted PDF. Includes all task names, completion dates, statuses, and notes. Print or email to surveyors and state regulators for inspection documentation.',
      },
      {
        name: '🤖 AI Compliance Summary (Enterprise)',
        location: 'Reports → AI Summary button',
        looks: 'AI Summary button; results appear in a modal with a narrative report',
        does: 'Claude analyzes all compliance entries for the selected period and generates an executive summary suitable for quality assurance meetings. Identifies patterns in missed tasks, highlights strong compliance areas, and flags items needing corrective action. Available on Enterprise plans only.',
      },
    ],
  },
  {
    id: 'security',
    label: 'Session & Security',
    icon: <Shield size={16} />,
    path: 'Automatic — applies to all users',
    summary: 'ProQure automatically manages session security to protect your facility data. Sessions are time-limited and will prompt you before expiring so you never lose unsaved work without warning.',
    features: [
      {
        name: 'Session Duration',
        location: 'Automatic — applies to all users',
        looks: 'No visible indicator during normal use',
        does: 'Sessions automatically expire after 4 hours of inactivity. If you leave the app open and idle, you will be logged out automatically to protect your account.',
      },
      {
        name: 'Session Expiry Warning',
        location: 'Full-screen modal overlay — appears automatically 5 minutes before logout',
        looks: 'Dark modal with a countdown timer showing MM:SS, plus "Stay Logged In" and "Log Out Now" buttons',
        does: 'A warning will appear 5 minutes before your session expires, giving you the option to stay logged in. The countdown shows exactly how much time remains before automatic logout.',
      },
      {
        name: 'Extending Your Session',
        location: 'Session warning modal → Stay Logged In button',
        looks: 'Blue "Stay Logged In" button in the warning modal',
        does: 'Click Stay Logged In to automatically refresh your session without losing any work. Your session resets to the full 4-hour window and the warning closes immediately.',
      },
      {
        name: 'Auto-Logout',
        location: 'Automatic — fires if the warning modal is ignored',
        looks: 'Page redirects to the login screen with a "Your session has expired" message',
        does: 'If no action is taken when the warning appears, you will be logged out and redirected to the login page. Any unsaved changes will be lost. Save your work regularly to avoid losing data.',
      },
      {
        name: 'Manual Logout',
        location: 'Sidebar → bottom — Logout button',
        looks: 'Logout button at the bottom of the navigation sidebar',
        does: 'You can log out at any time using the Logout button at the bottom of the sidebar. This immediately ends your session and returns you to the login page.',
      },
      {
        name: 'Re-logging In',
        location: 'Login page',
        looks: 'Standard login form with username and password fields',
        does: 'After being logged out, simply enter your credentials again. Your data is saved automatically — nothing is lost from your account when your session expires.',
      },
    ],
  },
];

// ─── Help Page ─────────────────────────────────────────────────────────────────

export default function HelpPage() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState('getting-started');

  const activeSection = SECTIONS.find((s) => s.id === activeId)!;

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'var(--bg-base)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 56,
        background: 'transparent',
        borderBottom: '1px solid var(--border-subtle)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <HelpCircle size={20} color="var(--accent-primary)" />
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>ProQure Help</h2>
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
            cursor: 'pointer', padding: '6px 12px', fontSize: '0.875rem',
          }}
        >
          <X size={15} /> Close
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left sidebar */}
        <div style={{
          width: 220, flexShrink: 0, overflowY: 'auto',
          borderRight: '1px solid var(--border-subtle)',
          background: 'transparent',
          padding: '12px 8px',
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              className={activeId === s.id ? 'sidebar-nav-item active' : 'sidebar-nav-item'}
              style={{ border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
            >
              {s.icon}
              <span style={{ flex: 1 }}>{s.label}</span>
              {activeId === s.id && <ChevronRight size={12} />}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>

          {/* Section header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                background: 'rgba(59,130,246,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--accent-primary)',
              }}>
                {activeSection.icon}
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
                  {activeSection.label}
                </h2>
                {activeSection.path && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                    <MapPin size={11} color="var(--text-muted)" />
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {activeSection.path}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9375rem', lineHeight: 1.7, margin: 0 }}>
              {activeSection.summary}
            </p>
          </div>

          {/* Feature cards */}
          {activeSection.features.length > 0 && (
            <>
              <div style={{
                fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 14,
              }}>
                Features
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {activeSection.features.map((f) => (
                  <div
                    key={f.name}
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      padding: '16px 20px',
                    }}
                  >
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9375rem', fontWeight: 600 }}>
                      {f.name}
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--text-muted)', minWidth: 90, fontWeight: 500 }}>Where:</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{f.location}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--text-muted)', minWidth: 90, fontWeight: 500 }}>Looks like:</span>
                        <span style={{ color: 'var(--text-secondary)' }}>{f.looks}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--text-muted)', minWidth: 90, fontWeight: 500 }}>What it does:</span>
                        <span style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{f.does}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* AI Features & Plan Tiers — Projects section only */}
          {activeId === 'projects' && (
            <div style={{ marginTop: 32 }}>
              <div style={{
                fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 14,
              }}>
                AI Features &amp; Plan Tiers
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                {/* Standard */}
                <div style={{
                  background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)', borderTop: '3px solid #64748b',
                  padding: '20px 18px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#64748b', marginBottom: 12 }}>
                    Standard Plan
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'Add and manage projects manually',
                      'Upload bid documents',
                      'Send basic approval emails with vendor comparison table and document links',
                      'No AI extraction',
                    ].map((item) => (
                      <li key={item} style={{ display: 'flex', gap: 8, fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        <Check size={13} style={{ flexShrink: 0, marginTop: 2, color: '#64748b' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Pro */}
                <div style={{
                  background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)', borderTop: '3px solid #3b82f6',
                  padding: '20px 18px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#3b82f6', marginBottom: 4 }}>
                    Pro Plan
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                    Everything in Standard, plus:
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'AI PDF proposal extraction — automatically reads vendor proposals and fills in bid details',
                      '9 AI extractions per facility per month (resets on the 1st)',
                      'Enhanced approval emails with full line items and scope of work per bid',
                      'Manual bid entry always available as fallback',
                    ].map((item) => (
                      <li key={item} style={{ display: 'flex', gap: 8, fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        <Check size={13} style={{ flexShrink: 0, marginTop: 2, color: '#3b82f6' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Enterprise */}
                <div style={{
                  background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-subtle)', borderTop: '3px solid #8b5cf6',
                  padding: '20px 18px',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#8b5cf6', marginBottom: 4 }}>
                    Enterprise Plan
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                    Everything in Pro, plus:
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      'Unlimited AI PDF extractions',
                      'AI Bid Analysis — compares all bids and provides recommendation on best price, best value, risks, and missing information',
                      'Full approval emails with AI analysis and recommendation included',
                      'Priority processing for scanned documents',
                    ].map((item) => (
                      <li key={item} style={{ display: 'flex', gap: 8, fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        <Check size={13} style={{ flexShrink: 0, marginTop: 2, color: '#8b5cf6' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
